import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { commissionAPI } from '../services/api';
import Pagination from '../components/Pagination';
import { SkeletonTable } from '../components/LoadingSkeleton';
import './Earnings.css';

function Earnings() {
  const [activeTab, setActiveTab] = useState('sales');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [payoutPage, setPayoutPage] = useState(1);

  const { data: earnings } = useQuery({
    queryKey: ['earnings'],
    queryFn: () => commissionAPI.getEarnings().then(res => res.data)
  });

  const { data: salesData, isLoading: salesLoading } = useQuery({
    queryKey: ['sales', page, limit],
    queryFn: () => commissionAPI.getSales({ page, limit }).then(res => res.data),
    keepPreviousData: true,
  });

  const { data: payoutsData, isLoading: payoutsLoading } = useQuery({
    queryKey: ['payouts', payoutPage],
    queryFn: () => commissionAPI.getPayouts({ page: payoutPage, limit: 20 }).then(res => res.data),
    keepPreviousData: true,
    enabled: activeTab === 'payouts',
  });

  const sales = salesData?.sales || [];
  const payouts = payoutsData?.payouts || [];

  return (
    <div className="earnings-page">
      <h1>Earnings Dashboard</h1>
      
      <div className="earnings-summary">
        <div className="earnings-card">
          <h3>Total Earnings</h3>
          <div className="amount">${earnings?.total_earnings?.toFixed(2) || '0.00'}</div>
        </div>
        <div className="earnings-card">
          <h3>Pending Payout</h3>
          <div className="amount pending">${earnings?.pending_payout?.toFixed(2) || '0.00'}</div>
        </div>
        <div className="earnings-card">
          <h3>Commission Rate</h3>
          <div className="amount">{(earnings?.commission_rate * 100)?.toFixed(1) || '10'}%</div>
        </div>
        <div className="earnings-card">
          <h3>Total Sales</h3>
          <div className="amount">{earnings?.confirmed_sales || 0}</div>
        </div>
      </div>
      
      <div className="earnings-tabs">
        <button 
          className={`earnings-tab ${activeTab === 'sales' ? 'active' : ''}`}
          onClick={() => setActiveTab('sales')}
        >
          Recent Sales
        </button>
        <button 
          className={`earnings-tab ${activeTab === 'payouts' ? 'active' : ''}`}
          onClick={() => setActiveTab('payouts')}
        >
          Payout History
        </button>
      </div>

      {activeTab === 'sales' && (
      <div className="sales-table-container">
        {salesLoading ? (
          <SkeletonTable rows={5} columns={5} />
        ) : sales.length > 0 ? (
          <>
            <table className="sales-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Date</th>
                  <th>Order Total</th>
                  <th>Commission</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {sales.map(sale => (
                  <tr key={sale.id}>
                    <td>{sale.order_id}</td>
                    <td>{new Date(sale.order_date).toLocaleDateString()}</td>
                    <td>${sale.order_total}</td>
                    <td>${sale.commission_amount}</td>
                    <td>
                      <span className={`status-badge ${sale.status}`}>
                        {sale.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {salesData?.pagination && (
              <Pagination
                page={page}
                totalPages={salesData.pagination.total_pages}
                total={salesData.pagination.total}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={(l) => { setLimit(l); setPage(1); }}
              />
            )}
          </>
        ) : (
          <p className="empty-state">No sales yet. Start sharing to earn commissions!</p>
        )}
      </div>
      )}

      {activeTab === 'payouts' && (
      <div className="sales-table-container">
        {payoutsLoading ? (
          <SkeletonTable rows={5} columns={4} />
        ) : payouts.length > 0 ? (
          <>
            <table className="sales-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map(payout => (
                  <tr key={payout.id}>
                    <td>{new Date(payout.created_at).toLocaleDateString()}</td>
                    <td>${Number(payout.amount).toFixed(2)}</td>
                    <td>{payout.method || 'N/A'}</td>
                    <td>
                      <span className={`status-badge ${payout.status}`}>
                        {payout.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {payoutsData?.pagination && (
              <Pagination
                page={payoutPage}
                totalPages={payoutsData.pagination.total_pages}
                total={payoutsData.pagination.total}
                limit={20}
                onPageChange={setPayoutPage}
              />
            )}
          </>
        ) : (
          <p className="empty-state">No payouts yet. Payouts will appear here once processed.</p>
        )}
      </div>
      )}
    </div>
  );
}

export default Earnings;
